import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { getCurrentUserData } from "@store/actions/auth.actions";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import { AdminNotice } from "@components/Settings/AdminNotice";
import { PermissionViewable, useUserPermissions } from "@components/PermissionedComponents";
import { isStaffEmail } from "@utils/user_utils";
import { useCurrentUserUuid } from "./rtpHooks";
import { PlansList } from "./PlansList";
import { PlanDetail } from "./PlanDetail";
import "@styles/styles-RtpPlanner.css";
import BlurredReturnToPlay from "./blurred_return_to_play.png";

// Copy of the Sandbox "access required" message (DashboardAltPage). Merge the two into one
// shared component when a third page needs it.
const NoRtpAccess = () => {
	const permissionsManagerList: { first_name: string; last_name: string }[] = useSelector(
		({ team }: any) => team?.info?.permissions_manager_list ?? []
	);
	return (
		<div className="rtp-no-access">
			<LockOutlined className="rtp-no-access-icon" />
			<Typography.Title level={4} style={{ margin: 0 }}>
				Return to Play access required
			</Typography.Title>
			<Typography.Text type="secondary">
				You do not have permission to view Return to Play.
				<br />
				Please reach out to your team's permissions manager to request access.
			</Typography.Text>
			{permissionsManagerList.length > 0 && (
				<Typography.Text>
					Permissions Manager{permissionsManagerList.length > 1 ? "s" : ""}:{" "}
					<strong>{permissionsManagerList.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}</strong>
				</Typography.Text>
			)}
		</div>
	);
};

const RtpPlannerContent = () => {
	const dispatch = useDispatch<any>();
	const currentUserId = useCurrentUserUuid();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		if (!currentUserId) {
			dispatch(getCurrentUserData());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openPlan = (id: number | null) => {
		const next = new URLSearchParams(searchParams);
		if (id) {
			next.set("plan", String(id));
		} else {
			next.delete("plan");
		}
		setSearchParams(next);
	};

	const planId = Number(searchParams.get("plan"));

	return planId > 0 ? (
		<PlanDetail key={planId} planId={planId} onBack={() => openPlan(null)} />
	) : (
		<PlansList onOpenPlan={openPlan} />
	);
};

// Every RTP API call starts under this root, so these gates stop all of them:
// 1. team info not loaded yet → nothing, 2. the feature is off for the team (staff pass with
// a notice) → the premium banner, 3. no ViewRtpProtocols permission → the access message.
export const RtpPlanner = () => {
	const { user } = useAuth0();
	const isStaff = isStaffEmail(user?.email);
	const featureEnabled = useSelector(({ team }: any) => Boolean(team?.info?.return_to_play_enabled));
	const userPermissions = useUserPermissions();

	if (userPermissions == null) return null;

	if (!featureEnabled && !isStaff) {
		return (
			<PremiumFeatureDisabled
				bg_image={BlurredReturnToPlay}
				show_navbar={false}
				feature_name="Return to Play"
				info_text={
					<>
						This is a premium feature that lets you plan each injured player's return in phases, with exit
						criteria, a block schedule, and progress against targets.
						<br />
						If you are interested in adding this to your platform, please contact your sales rep!
					</>
				}
			/>
		);
	}

	return (
		// .rtp-scope carries the feature's design tokens (see styles-RtpPlanner.css).
		<div className="rtp-scope">
			{!featureEnabled && <AdminNotice description={undefined} style={{ marginBottom: 16 }} />}
			<PermissionViewable
				permissionCheck="ViewRtpProtocols"
				permissionMessage={<NoRtpAccess />}
				tooltipMessage={undefined}
			>
				<RtpPlannerContent />
			</PermissionViewable>
		</div>
	);
};
